import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  TrendingUp, 
  Users, 
  Target,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import { API_URL } from '../../components/config';

const NotificationAnalyticsCard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchKpiData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/analytics/notifications/kpi-summary?period=7d`);
      const data = await response.json();

      if (data.status === 200) {
        setKpiData(data.data);
      } else {
        setError('Failed to load notification data');
      }
    } catch (err) {
      console.error('Error fetching notification KPI:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpiData();
  }, []);

  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUp className="h-3 w-3 text-green-500" />;
    if (current < previous) return <ArrowDown className="h-3 w-3 text-red-500" />;
    return <div className="h-3 w-3 bg-gray-300 rounded-full" />;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="animate-spin h-6 w-6 text-blue-500" />
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Push Notifications</h3>
          <p className="text-sm text-gray-600">Last 7 days</p>
        </div>
        <button
          onClick={fetchKpiData}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {kpiData && (
        <div className="space-y-4">
          {/* Total Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <Bell className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Sent</p>
                <p className="text-lg font-semibold text-gray-900">{kpiData.notifications.totalSent.toLocaleString()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Delivered</p>
              <p className="text-lg font-semibold text-green-600">{formatPercentage(kpiData.notifications.deliveryRate)}</p>
            </div>
          </div>

          {/* Match Rate */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg mr-3">
                <Target className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Match Rate</p>
                <p className="text-lg font-semibold text-gray-900">{formatPercentage(kpiData.matchRates.overall)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Open Rate</p>
              <p className="text-lg font-semibold text-purple-600">{formatPercentage(kpiData.notifications.openRate)}</p>
            </div>
          </div>

          {/* Performance */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Avg Delivery</p>
                <p className="text-lg font-semibold text-gray-900">{kpiData.performance.avgDeliveryTime}s</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Avg Open</p>
              <p className="text-lg font-semibold text-gray-900">{kpiData.performance.avgOpenTime}s</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm text-gray-600">Private Rides</p>
              <p className="text-base font-semibold text-blue-600">{formatPercentage(kpiData.matchRates.privateRides)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Rideshare</p>
              <p className="text-base font-semibold text-green-600">{formatPercentage(kpiData.matchRates.rideshare)}</p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default NotificationAnalyticsCard;
